import React, { useState, useEffect } from 'react';
import api from '../../api';
import './BlogsPage.css';

const GRADIENTS = [
  'linear-gradient(135deg, #5356e6 0%, #7b7def 60%, #a5a7f8 100%)',
  'linear-gradient(135deg, #f0c755 0%, #e8a020 60%, #c9a02e 100%)',
  'linear-gradient(135deg, #10b981 0%, #059669 60%, #047857 100%)',
  'linear-gradient(135deg, #ec4899 0%, #db2777 60%, #be185d 100%)'
];

const COLORS = [
  '#5356e6',
  '#c9a02e',
  '#059669',
  '#db2777'
];

export default function BlogsPage({ onNavigate }) {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await api.get('/blogs.php');
        const data = Array.isArray(res.data) ? res.data : [];
        // Sort newest first
        data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setBlogs(data);
      } catch (err) {
        console.error("Failed to load blogs:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  const getDomain = (urlStr) => {
    try {
      const url = new URL(urlStr);
      return url.hostname.replace('www.', '');
    } catch (e) {
      return 'External Source';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="blogs-page-dedicated">
      {/* ── Page Header & Breadcrumbs ── */}
      <header className="blogs-page__header">
        <div className="blogs-page__header-overlay" />
        <div className="blogs-page__header-content">
          <nav className="blogs-page__breadcrumb" aria-label="Breadcrumb">
            <span className="blogs-page__breadcrumb-item" onClick={() => onNavigate?.('home')}>
              Home
            </span>
            <span className="blogs-page__breadcrumb-separator">/</span>
            <span className="blogs-page__breadcrumb-item blogs-page__breadcrumb-item--active">
              Blogs &amp; Articles
            </span>
          </nav>
          <h1 className="blogs-page__title">Curated Library Blogs &amp; Articles</h1>
          <p className="blogs-page__subtitle">
            Expanding your learning horizon — read handpicked research tips, study guides, and literature insights.
          </p>
        </div>
      </header>

      {/* ── Main Grid Content ── */}
      <main className="blogs-page__main">
        {loading ? (
          <div className="blogs-page__loading">
            <i className="fa-solid fa-spinner fa-spin blogs-page__spinner"></i>
            <p>Loading curated articles...</p>
          </div>
        ) : blogs.length === 0 ? (
          <div className="blogs-page__empty">
            <i className="fa-solid fa-rss-slash blogs-page__empty-icon"></i>
            <h3>No Articles Published</h3>
            <p>We haven't shared any curated resource blogs yet. Check back soon!</p>
            <button className="blogs-page__back-btn" onClick={() => onNavigate?.('home')}>
              Back to Home
            </button>
          </div>
        ) : (
          <div className="blogs-page__grid">
            {blogs.map((blog, i) => {
              const gradient = GRADIENTS[i % 4];
              const categoryColor = COLORS[i % 4];
              const domain = getDomain(blog.link_url);
              const displayIcon = blog.icon || 'fa-solid fa-book-open';

              return (
                <article key={blog.id || i} className="blog-card-dedicated">
                  {/* Thumbnail */}
                  <div
                    className="blog-card-dedicated__thumb"
                    style={{ background: gradient }}
                  >
                    <div className="blog-card-dedicated__thumb-icon">
                      <i className={displayIcon}></i>
                    </div>
                    {/* External link corner badge */}
                    <div className="blog-card-dedicated__ext-badge">
                      <i className="fa-solid fa-arrow-up-right-from-square"></i>
                    </div>
                    {/* Category chip on image */}
                    <div
                      className="blog-card-dedicated__cat-chip"
                      style={{ background: `rgba(255,255,255,0.18)` }}
                    >
                      {domain}
                    </div>
                  </div>

                  {/* Body */}
                  <div className="blog-card-dedicated__body">
                    {/* Meta */}
                    <div className="blog-card-dedicated__meta">
                      <span className="blog-card-dedicated__source">
                        <i className="fa-solid fa-globe"></i>
                        {domain}
                      </span>
                      <span className="blog-card-dedicated__date">
                        <i className="fa-regular fa-calendar"></i>
                        {formatDate(blog.created_at)}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="blog-card-dedicated__title" title={blog.title}>
                      {blog.title}
                    </h3>

                    {/* Excerpt */}
                    <p className="blog-card-dedicated__excerpt" title={blog.description}>
                      {blog.description || 'No description summary available.'}
                    </p>

                    {/* CTA */}
                    <a
                      href={blog.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="blog-card-dedicated__cta"
                      style={{ '--blog-color': categoryColor }}
                    >
                      Read Article
                      <i className="fa-solid fa-arrow-up-right-from-square"></i>
                    </a>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>

      {/* ── View All CTA Note ── */}
      <div className="blogs-page__footer">
        <p className="blogs-page__footer-note">
          <i className="fa-solid fa-circle-info"></i>
          These articles are hosted on external websites and will open in a new browser tab.
        </p>
      </div>
    </div>
  );
}
